type ImageLoaderProps = {
  src: string;
  width: number;
  quality?: number;
  highDpi?: boolean;
};

// Parse an `ar` (aspect ratio) hint off the src and turn it into the HEIGHT of
// the Storyblok transform. Returns 0 — Storyblok's "derive from the source
// aspect" sentinel, and the value this loader has always emitted — whenever the
// hint is absent or unusable, so a src that does not opt in produces a
// byte-identical URL to before this parameter existed.
const resolveHeight = (ar: string | null, width: number) => {
  if (!ar) return 0;

  // A single guard on the RESULT covers every bad input: a non-numeric `ar`
  // gives NaN, `0` gives Infinity, and a negative gives a negative height.
  const height = Math.round(width / Number(ar));
  return Number.isFinite(height) && height > 0 ? height : 0;
};

export const storyblokLoader = ({
  src,
  width,
  quality,
  highDpi: highDpiProp,
}: ImageLoaderProps) => {
  // `new URL` throws on a relative src (a local `/public` asset, or a bundler
  // emitting `/assets/logo.svg`). Only absolute Storyblok URLs are transformed,
  // so anything unparseable is returned untouched instead of crashing the page.
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return src;
  }

  if (url.host !== "a.storyblok.com" && url.host !== "a-us.storyblok.com")
    return src;

  if (src.includes("svg")) return src;

  const focus = url.searchParams.get("focus");
  url.searchParams.delete("focus");

  // `m/{width}x0` preserves the SOURCE aspect ratio, so the delivered file only
  // ever has as much height as its own proportions imply. When the box an image
  // is painted into is taller than the source — every `object-cover` hero —
  // cover becomes height-driven and upscales, and no `sizes` hint can help
  // because `sizes` only ever influences width. Measured on
  // /en/about/advocacy-at-arrive-3: a 1080x393 file painted into a 900x770 box
  // is a 1.96x upscale (2.21x at DPR 2). Callers that know their box aspect opt
  // in with `?ar=`, which asks Storyblok for a real `m/{width}x{height}` crop
  // instead. Storyblok honours `focal()` under `m/WxH` (probed), so opting in
  // moves the crop to the CDN without losing the editor's focal point.
  const ar = url.searchParams.get("ar");
  url.searchParams.delete("ar");

  const highDpi = highDpiProp || url.searchParams.get("highDpi") === "1";
  url.searchParams.delete("highDpi");

  // A custom Next.js `loaderFile` bypasses the built-in format negotiation, so
  // we must ask Storyblok for a modern format explicitly — otherwise it serves
  // the original JPG/PNG. avif is the smallest modern format and directly
  // shrinks the LCP hero image (matches the video-poster optimizer).
  //
  // Cap quality at 75. AVIF compression degrades sharply past ~75: a CMS-set
  // quality(100) hero weighs ~196 KB vs ~23 KB at 75 (8.5x) and ~11 KB at the
  // default 50 — for an imperceptible visual delta on photographic content.
  // Editors had set quality(100) on hero images, making the AVIF the LCP
  // bottleneck (resource Load = 77–94% of LCP). Clamping here fixes every
  // affected page centrally and guards against future runaway values, while
  // leaving the default (unset → 50) path untouched.
  const MAX_QUALITY = 75;
  const resolvedQuality = Math.min(quality ?? 50, MAX_QUALITY);
  const filters = [`format(avif)`, `quality(${resolvedQuality})`];
  if (focus) filters.push(`focal(${focus})`);

  const w = highDpi ? width * 2 : width;
  const height = resolveHeight(ar, w);

  return `${url.origin}${url.pathname}/m/${w}x${height}/filters:${filters.join(":")}`;
};

/**
 * Build the src that `storyblokLoader` will later parse — the write half of the
 * same contract, kept beside the read half so the two cannot drift.
 *
 * Uses `URLSearchParams` rather than string concatenation. `focus` and `ar`
 * genuinely co-occur on hero images, and the concatenation this replaced
 * (`` `${src}?focus=${focus}` ``) would have produced `...?focus=x?ar=y` the
 * moment a second parameter existed — which `URL` parses as ONE `focus` whose
 * value is `x?ar=y`, silently losing the crop AND corrupting the focal point.
 *
 * With no params the src is returned unchanged, so a caller that opts into
 * nothing is byte-identical to one that never called this.
 */
export const withStoryblokParams = (
  src: string,
  {
    focus,
    ar,
    highDpi,
  }: { focus?: string | null; ar?: number | null; highDpi?: boolean } = {},
) => {
  const params = new URLSearchParams();
  if (focus) params.set("focus", focus);
  if (ar) params.set("ar", String(ar));

  if (highDpi) params.set("highDpi", "1");

  // `URLSearchParams` percent-encodes `:`, which the raw-interpolation this
  // replaced did not. That is invisible on the Storyblok path (the query is
  // parsed and discarded before the transform URL is rebuilt), but
  // `storyblokLoader` returns the src VERBATIM for SVGs and non-Storyblok
  // hosts — and a CMS asset can carry a focal point regardless of its type. On
  // those two paths the encoding would leak into the emitted `<img src>` and
  // break the guarantee that a caller opting into nothing gets a byte-identical
  // URL. `:` is legal unencoded in a query component (RFC 3986 §3.4), and
  // `URLSearchParams` round-trips it either way, so restore it.
  const query = params.toString().replace(/%3A/g, ":");
  if (!query) return src;

  return `${src}${src.includes("?") ? "&" : "?"}${query}`;
};
